// hooks
import { useTranslation } from 'react-i18next';
import useTimeFilter from '@/hooks/useTimeFilter';

// utils
import { getFilterCityIndex, getTimeContent } from '@/utils/content.utils';
import { buildTestId } from '@/utils/helpers.utils';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _map from 'lodash/map';

// styles
import Styled from './TimeFilter.styled';

// types
import type TimeFilterStore from '@tajawal/web-flights-sdk/lib/stores/filter/time-filter.store';

// components
import FilterTabs from '@/app-desktop/containers/Filters/components/FilterTabs';
import { Chips, color, COLOR_VARIANTS, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';
// TODO: replace and remove icons when we have the new icons from the design system
import { Morning } from './components/Morning';
import { MidDay } from './components/MidDay';
import { MidNight } from './components/MidNight';
import { Evening } from './components/Evening';

// constants
import { TIME_FILTER_SEGMENTS } from '@tajawal/web-flights-sdk/lib/constants';
import { FLIGHT_LEG_TYPE_MAP } from '@tajawal/web-flights-sdk/lib/constants/filters.constants';

export type TimeProps = {
  testIdPrefix: string;
  tabs: { code: number; label: string }[];
  requestAirports: string[][];
  filter: TimeFilterStore;
  isAMFormat: boolean;
  isStepByStep: boolean;
  currentStep: string;
};

function TimeFilter({
  testIdPrefix,
  tabs,
  requestAirports,
  filter,
  isAMFormat = true,
  currentStep,
  isStepByStep,
}: TimeProps): JSX.Element {
  const { t } = useTranslation();
  const testId = buildTestId(testIdPrefix, 'Time');
  const { activeIndex, originFilterState, destinationFilterState, handleToggleOption, setActiveIndex, resources } =
    useTimeFilter(filter);
  const cityIndex = getFilterCityIndex(isStepByStep, currentStep);

  const getIcon = (code: string, selected: boolean) => {
    const iconColor = selected ? color(COLOR_VARIANTS.AQUA100) : color(COLOR_VARIANTS.MINESHAFT);
    switch (code) {
      case TIME_FILTER_SEGMENTS.AM.code:
        return <Morning color={iconColor} />;
      case TIME_FILTER_SEGMENTS.AM2.code:
        return <MidDay color={iconColor} />;
      case TIME_FILTER_SEGMENTS.PM.code:
        return <Evening color={iconColor} />;
      case TIME_FILTER_SEGMENTS.PM2.code:
        return <MidNight color={iconColor} />;
      default:
        return null;
    }
  };

  return (
    <Styled.Wrapper>
      {!_isEmpty(tabs) && <FilterTabs tabs={tabs} activeIndex={activeIndex} testId={testId} onClick={setActiveIndex} />}
      <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
        {t('Filters.FlyFrom')}{' '}
        {resources.getAirportCity(_get(requestAirports, `${activeIndex}.${cityIndex.flyFrom}`, ''))}
      </Typography>
      <Styled.StateWrapper>
        {_map(originFilterState, ({ code, selected }) => {
          const label = getTimeContent(isAMFormat, code, t);
          return (
            <Chips
              sx={{ width: '135px', justifyContent: 'start', gap: '4px' }}
              size="regular"
              key={`${FLIGHT_LEG_TYPE_MAP.ORIGIN}_${code}`}
              type="action"
              onClick={() => handleToggleOption({ index: activeIndex, type: FLIGHT_LEG_TYPE_MAP.ORIGIN, code })}
              isSelected={selected}
              label={label}
              isClickable
              testId={buildTestId(testId, `${FLIGHT_LEG_TYPE_MAP.ORIGIN}_Option_${code}`)}
              icon={getIcon(code, selected)}
            />
          );
        })}
      </Styled.StateWrapper>
      <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
        {t('Filters.Arrivein')}{' '}
        {resources.getAirportCity(_get(requestAirports, `${activeIndex}.${cityIndex.arriveIn}`, ''))}
      </Typography>
      <Styled.StateWrapper>
        {_map(destinationFilterState, ({ code, selected }) => {
          const label = getTimeContent(isAMFormat, code, t);
          return (
            <Chips
              sx={{ width: '135px', justifyContent: 'start', gap: '4px' }}
              size="regular"
              key={`${FLIGHT_LEG_TYPE_MAP.DESTINATION}_${code}`}
              type="action"
              onClick={() => handleToggleOption({ index: activeIndex, type: FLIGHT_LEG_TYPE_MAP.DESTINATION, code })}
              isSelected={selected}
              label={label}
              isClickable
              testId={buildTestId(testId, `${FLIGHT_LEG_TYPE_MAP.DESTINATION}_Option_${code}`)}
              icon={getIcon(code, selected)}
            />
          );
        })}
      </Styled.StateWrapper>
    </Styled.Wrapper>
  );
}

export default TimeFilter;
